import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FontLink from "./components/FontLink";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import LoginScreen from "./LoginScreen";
import Dashboard from "./pages/Dashboard";
import ClientConfig from "./pages/ClientConfig";
import AllocationPage from "./pages/AllocationPage";
import Reviews from "./pages/Reviews";
import Scoring from "./pages/Scoring";
import EPRForms from "./pages/EPRForms";
import Scheduler from "./pages/Scheduler";
import { NewCycleModal } from "./pages/Scheduler";
import EmployeeDatabase from "./pages/EmployeeDatabase";
import Settings from "./pages/Settings";
import EmailModule from "./pages/EmailModule";
import EmailLogs from "./pages/EmailLogs";
import Reports from "./pages/Reports";
import { gapPct, buildNotifications } from "./constants";
import { apiFetch } from "./utils/api";

// ── Data normalizers ───────────────────────────────────────────────────────────
function normalizeClient(c) {
  return {
    ...c,
    id: c.id,
    code: c.code || c.id,
    name: c.name || "",
    industry: c.industry || "",
    status: c.status || "active",
    color: c.color_hex || c.color || "#64748B",
    color_hex: c.color_hex || c.color || "#64748B",
    primaryStakeholderId: c.primary_stakeholder_id ?? c.primaryStakeholderId ?? null,
    primaryContact: c.primaryContact || {
      name: [c.pc_first_name, c.pc_last_name].filter(Boolean).join(" ") || c.pc_name || "",
      firstName: c.pc_first_name || "",
      lastName: c.pc_last_name || "",
      email: c.pc_email || "",
      phone: c.pc_phone || "",
    },
    stakeholders: (c.stakeholders || []).map(s => {
      const firstName = s.first_name || s.firstName || (s.name || "").split(" ")[0] || "";
      const lastName = s.last_name || s.lastName || (s.name || "").split(" ").slice(1).join(" ") || "";
      const fullName = [firstName, lastName].filter(Boolean).join(" ") || s.name || "";
      return {
        ...s,
        deptId: s.dept_id ?? s.deptId ?? null,
        active: Boolean(s.active),
        firstName,
        lastName,
        name: fullName,
      };
    }),
    departments: c.departments || [],
    domains: (c.domains || []).map(d => typeof d === "string" ? { id: d, domain: d } : d),
    domain: c.primary_domain || c.domain || "",
  };
}

function normalizeCycle(c) {
  // Derive closed purely from status string — never from the DB closed column.
  // This ensures manually resetting status in DB to "Scheduled" takes effect on reload.
  const status = c.status ?? "Scheduled";
  const closed = status === "Closed";

  // The list endpoint returns `reminder_dates` as a comma-separated string
  // (GROUP_CONCAT from cycle_reminders, ordered by reminder_number). Parse it
  // into a clean ISO-date array so the Scheduler can render all N reminders.
  // Fall back to the inline r1/r2/r3 columns when reminder_dates is empty
  // (e.g. a cycle created before the cycle_reminders sync landed).
  let remindersArr;
  if (c.reminder_dates && typeof c.reminder_dates === "string") {
    remindersArr = c.reminder_dates.split(",").map(s => (s || "").trim().slice(0, 10)).filter(Boolean);
  } else if (Array.isArray(c.reminders)) {
    remindersArr = c.reminders.map(s => (s || "").slice(0, 10)).filter(Boolean);
  } else {
    remindersArr = [c.r1_date, c.r2_date, c.r3_date]
      .map(d => (d || "").slice(0, 10))
      .filter(Boolean);
  }

  return {
    ...c,
    q: c.quarter_label ?? c.q ?? "",
    quarter: c.quarter_num ?? c.quarter ?? 0,
    start: (c.start_date ?? c.start ?? "").slice(0, 10),
    // `end_date` is the new column name; fall back to legacy `deadline` so any
    // row still served from the pre-rename API also normalizes correctly.
    endDate: (c.end_date ?? c.deadline ?? c.endDate ?? "").slice(0, 10),
    // Keep r1/r2/r3 for any legacy code paths; reminders[] is the authoritative source.
    r1: remindersArr[0] || (c.r1_date ?? c.r1 ?? "").slice(0, 10),
    r2: remindersArr[1] || (c.r2_date ?? c.r2 ?? "").slice(0, 10),
    r3: remindersArr[2] || (c.r3_date ?? c.r3 ?? "").slice(0, 10),
    reminders: remindersArr,
    sent: Number(c.sent_count ?? c.sent ?? 0),
    submitted: Number(c.submitted_count ?? c.submitted ?? 0),
    approved: Number(c.approved_count ?? 0),
    // Total = every (employee, stakeholder) review row that exists for this
    // cycle, regardless of whether the Request Review was sent yet.
    totalReviews: Number(c.total_reviews ?? c.totalReviews ?? c.sent_count ?? 0),
    // Derived breakdown for the header pill:
    //   notStarted = total - sent (request never went out)
    //   pending    = sent - submitted - approved (in flight)
    //   submitted  = the submitted aggregate
    notStarted: Math.max(0, Number(c.total_reviews ?? 0) - Number(c.sent_count ?? 0)),
    pending: Math.max(0, Number(c.sent_count ?? 0) - Number(c.submitted_count ?? 0) - Number(c.approved_count ?? 0)),
    closed,
    status,
    emailHistory: c.emailHistory ?? [],
  };
}

function normalizeReview(r) {
  return {
    ...r,
    employee: r.employee_name || r.employee || "",
    empId: r.employee_code || r.empId || "",
    client: r.client_name || r.client || "",
    quarter: r.cycle_name || r.quarter || "",
    stakeholder: r.stakeholder_name || r.stakeholder || "",
    stakeholderEmail: r.stakeholder_email || r.stakeholderEmail || "",
    submittedAt: r.submitted_at
      ? new Date(r.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
      : (r.submittedAt || ""),
    hikeGiven: r.approved_hike != null ? String(r.approved_hike) : (r.hikeGiven || ""),
  };
}

function normalizeAllocEmployee(e) {
  return {
    id: e.employee_id,
    code: e.employee_code,
    name: e.employee_name,
    role: e.designation || "",
    ctc: 0,
    allocations: (e.allocations || [])
      .filter(a => a.allocation_id != null)
      .map(a => ({
        clientId: a.client_id,
        clientName: a.client_name,
        deptId: a.dept_id,
        pct: a.pct,
        color: a.color_hex || "#64748B",
        stakeholders: (a.stakeholders || []).map(s => ({
          stakeholderId: s.stakeholder_id,
          pct: s.review_pct,
          name: s.stakeholder_name || "",
          email: s.stakeholder_email || "",
        })),
      })),
  };
}

// ── Employee list normalizer (list endpoint returns snake_case) ────────────────
function normalizeEmpListItem(e) {
  return {
    ...e,
    designation: e.designation || e.role || "",
    firstName: e.first_name || "",
    lastName: e.last_name || "",
    officialEmail: e.official_email || e.officialEmail || "",
    personalEmail: e.personal_email || e.personalEmail || "",
    primaryPhone: e.primary_phone || e.primaryPhone || "",
    secondaryPhone: e.secondary_phone || e.secondaryPhone || "",
    gender: e.gender || "",
    dob: e.dob ? String(e.dob).split("T")[0] : (e.dob || ""),
    bloodGroup: e.blood_group || e.bloodGroup || "",
    nationality: e.nationality || "",
    aadhaar: e.aadhaar_number || e.aadhaar || "",
    pan: e.pan_number || e.pan || "",
    joinDate: e.joining_date
      ? String(e.joining_date).split("T")[0]
      : (e.joinDate || ""),
    reportingManager: e.reporting_manager || e.reportingManager || "",
    status: e.active != null
      ? (Number(e.active) ? "Active" : "Inactive")
      : (e.status || "Active"),
    currentAddr: (e.curr_addr_line1 || e.curr_addr_city)
      ? { line1: e.curr_addr_line1 || "", line2: e.curr_addr_line2 || "", city: e.curr_addr_city || "", state: e.curr_addr_state || "", pin: e.curr_addr_pincode || "" }
      : (e.currentAddr || { line1: "", line2: "", city: "", state: "", pin: "" }),
    permanentAddr: e.permanentAddr || { line1: "", line2: "", city: "", state: "", pin: "" },
    emergency: e.ec_first_name
      ? { name: [e.ec_first_name, e.ec_last_name].filter(Boolean).join(" "), firstName: e.ec_first_name || "", lastName: e.ec_last_name || "", relation: e.ec_relation || "", phone: e.ec_phone || "", email: e.ec_email || "" }
      : (e.emergency || { name: "", firstName: "", lastName: "", relation: "", phone: "", email: "" }),
    education: Array.isArray(e.education) ? e.education : [],
    workHistory: Array.isArray(e.workHistory) ? e.workHistory : [],
    skills: e.skills_csv
      ? e.skills_csv.split(", ").filter(Boolean)
      : (Array.isArray(e.skills)
        ? e.skills.map(s => typeof s === "string" ? s : (s.skill_name || ""))
        : []),
  };
}

const VALID_PAGES = ["dashboard", "clients", "resources", "employees", "reviews", "scoring", "eprforms", "scheduler", "email", "emaillogs", "reports", "settings"];
const SUB_ADMIN_PAGES = ["employees", "resources", "scheduler", "reports"];

export default function DolluzEPRPortal() {
  const navigate = useNavigate();
  const location = useLocation();

  const [adminProfile, setAdminProfile] = useState({
    name: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    designation: "",
    company: "",
    timezone: "",
    avatar: "",
    role: "",
  });
  const [requirePasswordReset, setRequirePasswordReset] = useState(false);

  const currentRole = { super_admin: "Super Admin", admin: "Admin", sub_admin: "Sub-Admin" }[adminProfile.role] || "Viewer";
  const isSubAdmin = adminProfile.role === "sub_admin";

  // Derive current page from URL path (e.g. /dashboard → "dashboard")
  const pageFromUrl = location.pathname.replace(/^\//, "") || "dashboard";
  const [page, setPageState] = useState(VALID_PAGES.includes(pageFromUrl) ? pageFromUrl : "dashboard");

  // Navigate + update state together (sub-admins are restricted to SUB_ADMIN_PAGES)
  const setPage = useCallback((p) => {
    const target = (adminProfile.role === "sub_admin" && !SUB_ADMIN_PAGES.includes(p)) ? "employees" : p;
    setPageState(target);
    navigate("/" + target, { replace: false });
  }, [navigate, adminProfile.role]);

  const [signedOut, setSignedOut] = useState(!localStorage.getItem("epr_token"));

  // Global 401 handler — if any API call gets unauthorized, force back to login
  useEffect(() => {
    const handle401 = () => {
      setSignedOut(true);
      setAppLoading(false);
      navigate("/", { replace: true });
    };
    // Global PASSWORD_RESET_REQUIRED handler — show banner, redirect to Settings
    const handlePwReset = () => {
      setRequirePasswordReset(true);
      navigate("/settings", { replace: false });
    };
    window.addEventListener("epr:unauthorized", handle401);
    window.addEventListener("epr:password-reset-required", handlePwReset);
    return () => {
      window.removeEventListener("epr:unauthorized", handle401);
      window.removeEventListener("epr:password-reset-required", handlePwReset);
    };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync state when user navigates with browser back/forward
  // Also handle /login URL: redirect to /dashboard if signed in, else show LoginScreen
  useEffect(() => {
    const p = location.pathname.replace(/^\//, "") || "dashboard";
    if (p === "login") {
      if (!signedOut) navigate("/dashboard", { replace: true });
      return;
    }
    if (VALID_PAGES.includes(p) && p !== page) setPageState(p);
  }, [location.pathname, signedOut]); // eslint-disable-line react-hooks/exhaustive-deps
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [showNewCycle, setShowNewCycle] = useState(false);
  const [empList, setEmpList] = useState([]);
  const [showAddEmp, setShowAddEmp] = useState(false);
  // Show loading spinner on hard-reload when already authenticated; for post-login flow
  // maybeReady() inside the data-load effect will clear this.
  const [appLoading, setAppLoading] = useState(!!localStorage.getItem("epr_token"));
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [cycleEmailState, setCycleEmailState] = useState({});
  const [bulkRequestedCycles, setBulkRequestedCycles] = useState({});
  const [allReviews, setAllReviews] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [settingsTab, setSettingsTab] = useState(0);
  const [scoringHikes, setScoringHikes] = useState({});
  const [scoringLocked, setScoringLocked] = useState({});

  // ── Load all shared data from backend on mount ─────────────────────────────
  useEffect(() => {
    if (signedOut) return;

    let clientsDone = false;
    let allocsDone = false;
    const maybeReady = () => { if (clientsDone && allocsDone) setAppLoading(false); };

    // Clients — fetch list then full data per client (to get stakeholders + departments)
    apiFetch("/api/clients")
      .then(r => r.json())
      .then(async d => {
        if (!d.success || !d.data) return;
        const fullClients = await Promise.all(
          d.data.map(c =>
            apiFetch(`/api/clients/${c.id}`)
              .then(r2 => r2.json())
              .then(d2 => d2.success && d2.data ? normalizeClient(d2.data) : normalizeClient(c))
              .catch(() => normalizeClient(c))
          )
        );
        setClients(fullClients);
      })
      .catch(() => { })
      .finally(() => { clientsDone = true; maybeReady(); });

    // Allocations — use as source of truth for employees (has full allocation shapes)
    apiFetch("/api/allocations")
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data) setEmployees(d.data.map(normalizeAllocEmployee));
      })
      .catch(() => { })
      .finally(() => { allocsDone = true; maybeReady(); });

    // Employees full list for EmployeeDatabase page
    apiFetch("/api/employees?limit=500")
      .then(r => r.json())
      .then(d => { if (d.success && d.data) setEmpList(d.data.map(normalizeEmpListItem)); })
      .catch(() => { });

    // Cycles + email dispatch state for active cycle
    apiFetch("/api/cycles")
      .then(r => r.json())
      .then(d => {
        if (!d.success || !d.data) return;
        const normalized = d.data.map(normalizeCycle);
        setCycles(normalized);

        // Bug 4 fix: restore bulkRequestedCycles from cycles that already have bulk_requested_at in DB
        const bulkMap = {};
        normalized.forEach(c => {
          if (c.bulk_requested_at) bulkMap[c.id] = c.bulk_requested_at;
        });
        if (Object.keys(bulkMap).length > 0) setBulkRequestedCycles(bulkMap);

        // Q7 fix: load email dispatch state for ALL cycles (not just active)
        // so email history shows correctly when switching to closed cycles in Reviews/Scheduler
        normalized.forEach(cycle => {
          apiFetch(`/api/email-dispatch/${cycle.id}`)
            .then(r => r.json())
            .then(d2 => {
              if (d2.success && d2.data && Object.keys(d2.data).length > 0)
                setCycleEmailState(prev => ({ ...prev, ...d2.data }));
            })
            .catch(() => { });
        });
      })
      .catch(() => { });

    // Reviews — normalize API field names to UI-expected names
    apiFetch("/api/announcements")
      .then(r => r.json())
      .then(d => { if (d.success && d.data) setAnnouncements(d.data); })
      .catch(() => { });

    apiFetch("/api/reviews")
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data) setAllReviews(d.data.map(normalizeReview));
      })
      .catch(() => { });

    // Email templates + CC
    apiFetch("/api/email-templates")
      .then(r => r.json())
      .then(d => { if (d.success && d.data) setEmailTemplates(d.data); })
      .catch(() => { });

    apiFetch("/api/email-templates/cc")
      .then(r => r.json())
      .then(d => { if (d.success && d.data) setCcList(d.data); })
      .catch(() => { });

    // Admin profile — if /api/auth/me can't return a valid user, the session is
    // unusable: bounce to login instead of rendering protected pages with an
    // empty admin profile (blank sidebar, no role, broken role checks).
    const forceSignOut = () => {
      localStorage.removeItem("epr_token");
      setSignedOut(true);
      setAppLoading(false);
      navigate("/", { replace: true });
    };
    apiFetch("/api/auth/me")
      .then(r => r.json())
      .then(d => {
        const u = d.user || d.data;
        if (d.success && u) {
          setAdminProfile(p => ({
            ...p,
            id: u.id ?? p.id,
            name: u.name || [u.first_name, u.last_name].filter(Boolean).join(" ") || p.name,
            firstName: u.first_name || p.firstName || "",
            lastName: u.last_name || p.lastName || "",
            email: u.email || p.email,
            phone: u.phone || p.phone,
            designation: u.designation || p.designation,
            timezone: u.timezone || p.timezone,
            company: u.company || p.company,
            role: u.role || p.role,
            avatar: (u.avatar_initials) || (u.name || p.name || "?")[0].toUpperCase(),
          }));
        } else {
          forceSignOut();
        }
      })
      .catch(forceSignOut);
  }, [signedOut]); // eslint-disable-line

  // Re-fetch relevant data whenever the user navigates to a page via sidebar
  useEffect(() => {
    if (signedOut) return;

    const refreshPage = page;

    if (refreshPage === "clients") {
      apiFetch("/api/clients").then(r => r.json()).then(d => {
        if (!d.success || !d.data) return;
        const base = d.data.map(normalizeClient);
        setClients(base);
        base.forEach(cl => {
          apiFetch(`/api/clients/${cl.id}`).then(r => r.json()).then(dd => {
            if (dd.success && dd.data) setClients(p => p.map(x => x.id === cl.id ? { ...x, ...normalizeClient(dd.data) } : x));
          }).catch(() => { });
        });
      }).catch(() => { });
    }

    if (refreshPage === "resources") {
      apiFetch("/api/allocations").then(r => r.json()).then(d => {
        if (d.success && d.data) setEmployees(d.data.map(normalizeAllocEmployee));
      }).catch(() => { });
    }

    if (refreshPage === "reviews" || refreshPage === "scoring" || refreshPage === "scheduler") {
      apiFetch("/api/reviews").then(r => r.json()).then(d => {
        if (d.success && d.data) setAllReviews(d.data.map(normalizeReview));
      }).catch(() => { });
      apiFetch("/api/cycles").then(r => r.json()).then(d => {
        if (!d.success || !d.data) return;
        const normalized = d.data.map(normalizeCycle);
        setCycles(normalized);
        // Refresh email-dispatch state for ALL cycles (not just one Active one).
        // Multiple cycles can be Active simultaneously, and Closed cycles still
        // need their state visible on Reviews/Scheduler. Merge rather than
        // replace so we don't wipe other cycles' state.
        normalized.forEach(cycle => {
          apiFetch(`/api/email-dispatch/${cycle.id}`).then(r => r.json()).then(dd => {
            if (dd.success && dd.data && Object.keys(dd.data).length > 0)
              setCycleEmailState(prev => ({ ...prev, ...dd.data }));
          }).catch(() => { });
        });
      }).catch(() => { });
    }

    if (refreshPage === "reports") {
      apiFetch("/api/reviews").then(r => r.json()).then(d => {
        if (d.success && d.data) setAllReviews(d.data.map(normalizeReview));
      }).catch(() => { });
    }

    if (refreshPage === "dashboard") {
      apiFetch("/api/reviews").then(r => r.json()).then(d => {
        if (d.success && d.data) setAllReviews(d.data.map(normalizeReview));
      }).catch(() => { });
      apiFetch("/api/cycles").then(r => r.json()).then(d => {
        if (d.success && d.data) setCycles(d.data.map(normalizeCycle));
      }).catch(() => { });
    }

  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // When role loads (fresh login or hard-refresh), redirect users away from
  // pages they aren't allowed to see:
  //   • sub_admin → restricted to SUB_ADMIN_PAGES
  //   • non-super_admin → cannot visit /emaillogs
  useEffect(() => {
    if (!adminProfile.role) return;
    if (adminProfile.role === "sub_admin" && !SUB_ADMIN_PAGES.includes(page)) {
      setPageState("employees");
      navigate("/employees", { replace: true });
    } else if (page === "emaillogs" && adminProfile.role !== "super_admin") {
      setPageState("dashboard");
      navigate("/dashboard", { replace: true });
    }
  }, [adminProfile.role, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const leakageCount = employees.filter(e => gapPct(e) > 0).length;
  const reviewCount = allReviews.filter(r => ["Not Started", "Initiated", "In Progress"].includes(r.status)).length;
  // Merge DB announcements (not read by current user) into notification list
  const annNotifs = announcements
    .filter(a => !a.is_read_by_me)
    .map(a => ({
      id: "ann_" + a.id,
      type: a.type === "alert" ? "overdue" : a.type === "warning" ? "deadline" : "info",
      title: a.title,
      body: a.body,
      page: "settings",
      tab: 2,
      time: a.created_at ? new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "Recently",
    }));
  const notifications = [...annNotifs, ...buildNotifications(employees, cycles, allReviews)];

  const onNewEmployee = emp => {
    // Update allocation employees list (used by Resources / Reviews / Scoring)
    setEmployees(p => [...p, {
      id: emp.id,
      code: emp.code,
      name: emp.name,
      role: emp.designation || "",
      ctc: emp.ctc || 0,
      allocations: [],
    }]);
    // NOTE: empList is already updated by EmployeeDatabase via the shared setEmpList prop.
    // Do NOT push here again — that causes a duplicate row until reload.
  };

  const handleAddCycle = nc => {
    setCycles(p => [...p, nc]);
    setShowNewCycle(false);
    setPage("scheduler");
  };

  const topBarProps = {
    onNewCycle: () => setShowNewCycle(true),
    notifications,
    setPage,
    onSetPageTab: (tab) => setSettingsTab(tab),
    onMarkAnnouncementRead: (annId) => {
      setAnnouncements(prev => prev.map(a => a.id === annId ? { ...a, is_read_by_me: true } : a));
    },
    onMarkAllAnnouncementsRead: () => {
      setAnnouncements(prev => prev.map(a => ({ ...a, is_read_by_me: true })));
    },
  };

  const pages = {
    dashboard: <Dashboard employees={employees} setPage={setPage} topBarProps={topBarProps} allReviews={allReviews} clients={clients} cycles={cycles} />,
    clients: <ClientConfig clients={clients} setClients={setClients} employees={employees} allReviews={allReviews} topBarProps={topBarProps} />,
    resources: <AllocationPage clients={clients} employees={employees} setEmployees={setEmployees} topBarProps={topBarProps}
      onAddNewEmployee={() => { setPage("employees"); setShowAddEmp(true); }} currentRole={currentRole} />,
    employees: <EmployeeDatabase topBarProps={topBarProps} empList={empList} setEmpList={setEmpList}
      openAddModal={showAddEmp} onAddModalClosed={() => setShowAddEmp(false)} onNewEmployee={onNewEmployee} currentRole={currentRole} />,
    reviews: <Reviews employees={employees} clients={clients} cycles={cycles} cycleEmailState={cycleEmailState} setCycleEmailState={setCycleEmailState} allReviews={allReviews} setAllReviews={setAllReviews} emailTemplates={emailTemplates} scoringLocked={scoringLocked} bulkRequestedCycles={bulkRequestedCycles} setBulkRequestedCycles={setBulkRequestedCycles} topBarProps={topBarProps} />,
    scoring: <Scoring topBarProps={topBarProps} cycles={cycles} setCycles={setCycles} clients={clients} employees={employees} cycleEmailState={cycleEmailState} allReviews={allReviews} setAllReviews={setAllReviews} scoringHikes={scoringHikes} setScoringHikes={setScoringHikes} scoringLocked={scoringLocked} setScoringLocked={setScoringLocked} />,
    eprforms: <EPRForms topBarProps={topBarProps} currentRole={currentRole} />,
    scheduler: <Scheduler employees={employees} cycles={cycles} setCycles={setCycles} clients={clients} topBarProps={topBarProps} cycleEmailState={cycleEmailState} setCycleEmailState={setCycleEmailState} currentRole={currentRole} allReviews={allReviews} emailTemplates={emailTemplates} />,
    email: <EmailModule cycles={cycles} clients={clients} employees={employees} emailTemplates={emailTemplates} setEmailTemplates={setEmailTemplates} ccList={ccList} setCcList={setCcList} cycleEmailState={cycleEmailState} currentRole={currentRole} topBarProps={topBarProps} />,
    emaillogs: <EmailLogs topBarProps={topBarProps} />,
    reports: <Reports topBarProps={topBarProps} employees={employees} allReviews={allReviews} clients={clients} cycles={cycles} />,
    settings: <Settings topBarProps={topBarProps} profile={adminProfile} setProfile={setAdminProfile} clients={clients} currentRole={currentRole} announcements={announcements} setAnnouncements={setAnnouncements} initialTab={settingsTab} requirePasswordReset={requirePasswordReset} onPasswordChanged={() => setRequirePasswordReset(false)} />,
  };

  return (
    <>
      <FontLink />

      {signedOut ? (
        <LoginScreen onLogin={(tok, user) => {
          // Do NOT set appLoading false here — the data-load useEffect
          // (triggered by setSignedOut(false)) will call setAppLoading(false)
          // via maybeReady() once clients + allocs are fetched.
          setAppLoading(true);
          setSignedOut(false);
          const role = user?.role || "";
          if (user) setAdminProfile(p => ({
            ...p,
            name: user.name || [user.first_name, user.last_name].filter(Boolean).join(" ") || p.name,
            firstName: user.first_name || p.firstName || "",
            lastName: user.last_name || p.lastName || "",
            email: user.email || p.email,
            role,
            avatar: (user.name || p.name || "?")[0].toUpperCase(),
          }));
          // Track if this user must reset their temporary password
          if (user?.requirePasswordReset) setRequirePasswordReset(true);
          // Sub-admins land on employees; everyone else on dashboard
          const landingPage = role === "sub_admin" ? "employees" : "dashboard";
          navigate("/" + landingPage, { replace: true });
        }} />
      ) : appLoading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#F0F4F8", flexDirection: "column", gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", border: "4px solid #E8520A", borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
          <div style={{ fontSize: 14, color: "#64748B", fontWeight: 600 }}>Loading portal…</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
        <div style={{ display: "flex", minHeight: "100vh", background: "#F0F4F8", flexDirection: "column" }}>
          {requirePasswordReset && (
            <div style={{
              position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
              background: "#FEF3C7", borderBottom: "2px solid #F59E0B",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 24px", gap: 12,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>⚠️</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#92400E" }}>
                  You are using a temporary password. Please{" "}
                  <button onClick={() => { navigate("/settings", { replace: false }); }}
                    style={{ background: "none", border: "none", color: "#B45309", fontWeight: 700, fontSize: 13, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                    go to Settings → Change Password
                  </button>{" "}
                  to set a new password before continuing.
                </span>
              </div>
            </div>
          )}
          <div style={{ display: "flex", flex: 1, marginTop: requirePasswordReset ? 44 : 0 }}>
            <Sidebar active={page} setActive={setPage} leakageCount={leakageCount} reviewCount={reviewCount}
              onSignOut={() => { localStorage.removeItem("epr_token"); setSignedOut(true); setAppLoading(false); navigate("/", { replace: true }); }}
              profile={adminProfile} setProfile={setAdminProfile} role={adminProfile.role} isSubAdmin={isSubAdmin} />
            <main style={{ marginLeft: 236, flex: 1, padding: "28px 32px", minHeight: "100vh" }}>
              {pages[page]}
            </main>
          </div>
        </div>
      )}

      {showNewCycle && (
        <NewCycleModal
          onAdd={handleAddCycle}
          onClose={() => setShowNewCycle(false)}
          existing={cycles}
        />
      )}
    </>
  );
}